import React, { useEffect, useRef, useState } from 'react';
import { Globe, X } from 'lucide-react';

/**
 * A calm, trauma-informed helper that explains how to translate the whole
 * site using the reader's own browser. No API, no tracking, no cost — the
 * translation happens entirely on the reader's device.
 *
 * Sits in the header next to the Welsh (Cymraeg) toggle.
 */
export default function TranslateHelper() {
  const [open, setOpen] = useState(false);
  const popoverRef = useRef(null);
  const buttonRef = useRef(null);

  // Detect the user's browser so we can show the most relevant instruction first.
  const primaryHint = getPrimaryHint();

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    const onClickAway = (e) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onClickAway);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onClickAway);
    };
  }, [open]);

  return (
    <div style={{ position: 'relative' }}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="translate-helper-toggle"
        aria-label="Read this page in another language"
        aria-expanded={open}
        aria-haspopup="dialog"
        className="inline-flex items-center justify-center rounded-full tf-focus"
        style={{
          background: '#fff',
          color: 'var(--tf-purple-dark)',
          border: '1px solid var(--tf-purple-light)',
          width: 40,
          height: 40,
          cursor: 'pointer',
          flexShrink: 0,
        }}
      >
        <Globe size={16} aria-hidden="true" focusable="false" />
      </button>

      {open && (
        <div
          ref={popoverRef}
          role="dialog"
          aria-label="Translate this page"
          data-testid="translate-helper-popover"
          className="tf-fade-up"
          style={{
            position: 'absolute',
            top: 'calc(100% + 10px)',
            right: 0,
            width: 320,
            maxWidth: 'calc(100vw - 24px)',
            background: '#fff',
            border: '1px solid var(--tf-card-border)',
            borderRadius: 16,
            boxShadow: '0 20px 40px rgba(43, 22, 78, 0.14)',
            padding: 18,
            zIndex: 60,
            color: 'var(--tf-text)',
          }}
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <p
                className="font-body font-bold"
                style={{
                  fontSize: 10.5,
                  letterSpacing: '0.22em',
                  color: 'var(--tf-purple-dark)',
                }}
              >
                READ IN ANOTHER LANGUAGE
              </p>
              <h3
                className="font-heading mt-1"
                style={{
                  fontSize: 17,
                  lineHeight: 1.25,
                  color: 'var(--tf-text)',
                  letterSpacing: '-0.01em',
                }}
              >
                Your browser can translate this page.
              </h3>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close translate helper"
              data-testid="translate-helper-close"
              className="tf-focus"
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--tf-text-muted)',
                cursor: 'pointer',
                padding: 2,
                flexShrink: 0,
              }}
            >
              <X size={16} aria-hidden="true" />
            </button>
          </div>

          <p
            className="font-body mt-3"
            style={{ fontSize: 13, lineHeight: 1.55, color: 'var(--tf-text-secondary)' }}
          >
            Translation happens on your device — nothing is sent to us, and no
            account is needed.
          </p>

          <p
            className="font-body font-bold mt-4"
            style={{ fontSize: 12, color: 'var(--tf-text)' }}
          >
            {primaryHint.label}
          </p>
          <p
            className="font-body"
            style={{
              fontSize: 12.5,
              lineHeight: 1.55,
              color: 'var(--tf-text-secondary)',
              marginTop: 2,
            }}
          >
            {primaryHint.step}
          </p>

          <details className="mt-4" style={{ cursor: 'pointer' }}>
            <summary
              className="font-body font-bold"
              style={{
                fontSize: 12,
                color: 'var(--tf-purple-dark)',
                listStyle: 'none',
              }}
            >
              How to do it on other browsers →
            </summary>
            <ul
              className="mt-3 flex flex-col gap-2 font-body"
              style={{
                fontSize: 12,
                lineHeight: 1.5,
                color: 'var(--tf-text-secondary)',
                paddingLeft: 0,
                listStyle: 'none',
              }}
            >
              <li>
                <strong style={{ color: 'var(--tf-text)' }}>Chrome / Edge (desktop):</strong>{' '}
                right-click the page, then <em>Translate to…</em>
              </li>
              <li>
                <strong style={{ color: 'var(--tf-text)' }}>Safari (Mac):</strong>{' '}
                click the translate icon in the address bar, or{' '}
                <em>View → Translate</em>
              </li>
              <li>
                <strong style={{ color: 'var(--tf-text)' }}>Chrome (Android):</strong>{' '}
                tap the ⋮ menu, then <em>Translate…</em>
              </li>
              <li>
                <strong style={{ color: 'var(--tf-text)' }}>Safari (iPhone / iPad):</strong>{' '}
                tap the <em>AA</em> button in the address bar, then{' '}
                <em>Translate to…</em>
              </li>
              <li>
                <strong style={{ color: 'var(--tf-text)' }}>Firefox:</strong>{' '}
                click the translate icon in the address bar (Firefox 118+), or
                install the <em>Firefox Translations</em> add-on.
              </li>
            </ul>
          </details>

          <p
            className="font-body mt-4"
            style={{
              fontSize: 11.5,
              lineHeight: 1.5,
              color: 'var(--tf-text-muted)',
            }}
          >
            Machine translation can be imperfect. For essential legal or safety
            information, please check with a qualified interpreter — most UK
            services below can arrange one at no cost.
          </p>
        </div>
      )}
    </div>
  );
}

function getPrimaryHint() {
  if (typeof navigator === 'undefined') {
    return {
      label: 'On most browsers:',
      step: 'Right-click the page and choose "Translate to…" — or use the browser menu.',
    };
  }
  const ua = navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isAndroid = /Android/.test(ua);
  const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
  const isFirefox = /Firefox\//.test(ua);
  const isEdge = /Edg\//.test(ua);
  const isChrome = /Chrome\//.test(ua) && !isEdge;

  if (isIOS) {
    return {
      label: 'On this iPhone or iPad:',
      step: 'Tap the AA button in the address bar, then choose "Translate to…".',
    };
  }
  if (isAndroid) {
    return {
      label: 'On this Android device:',
      step: 'Tap the ⋮ menu (top right), then choose "Translate…".',
    };
  }
  if (isSafari) {
    return {
      label: 'On this Mac (Safari):',
      step: 'Click the translate icon in the address bar, or use View → Translate.',
    };
  }
  if (isFirefox) {
    return {
      label: 'On Firefox:',
      step: 'Click the translate icon in the address bar (Firefox 118+), or install the Firefox Translations add-on.',
    };
  }
  if (isChrome || isEdge) {
    return {
      label: 'On this browser:',
      step: 'Right-click anywhere on the page, then choose "Translate to…".',
    };
  }
  return {
    label: 'On most browsers:',
    step: 'Right-click the page and choose "Translate to…", or use the browser menu.',
  };
}
